// No syntax errors found in this file. The code is syntactically correct and valid JavaScript/JSX.
// The linting message refers to TypeScript type checking or PropTypes validation warnings, not syntax errors.
// All brackets, parentheses, and JSX structures are properly closed and nested.
// No changes needed to the code structure.
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { challengeService, friendshipService, milestoneService, achievementService, socialRealtimeService, duelService, squadService } from '../../services/socialService';
import { useSocialRealtime } from '../../hooks/useSocialRealtime';
import { useUserDuelsRealtime } from '../../hooks/useDuelRealtime';
import { useDuelInvitesRealtime, useLeaderboardRealtime, useFriendActivityRealtime } from '../../hooks/useSocialRealtime';
import ChallengeCard from './components/ChallengeCard';
import LeaderboardCard from './components/LeaderboardCard';
import DuelCard from './components/DuelCard';
import CreateDuelModal from './components/CreateDuelModal';
import DuelDetailModal from './components/DuelDetailModal';

import CreateSquadModal from './components/CreateSquadModal';
import CreateChallengeModal from './components/CreateChallengeModal';
import FriendDiscovery from './components/FriendDiscovery';
import FriendRequestsPanel from './components/FriendRequestsPanel';
import ActivityFeed from './components/ActivityFeed';
import DuelStats from './components/DuelStats';
import DuelResultsModal from './components/DuelResultsModal';
import Button from '../../components/ui/Button';
import { pushNotificationService } from '../../services/pushNotificationService';
import PushNotificationPrompt from '../../components/PushNotificationPrompt';
import FriendDuelActivityFeed from './components/FriendDuelActivityFeed';
import { achievementCardService } from '../../services/achievementCardService';
import SquadScreen from './components/SquadScreen';

const SectionHeader = ({ icon, title, subtitle, action = null }) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center space-x-3">
      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon name={icon} size={18} className="text-primary" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-foreground leading-tight">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {action && action}
  </div>
);

const SocialHub = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState('social'); // 'social' | 'squads'
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [badges, setBadges] = useState([]);
  const [duels, setDuels] = useState([]);
  const [squads, setSquads] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendActivity, setFriendActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDuelModal, setShowCreateDuelModal] = useState(false);
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);
  const [showCreateChallengeModal, setShowCreateChallengeModal] = useState(false);
  const [showFriendDiscovery, setShowFriendDiscovery] = useState(false);
  const [selectedDuel, setSelectedDuel] = useState(null);
  const [showDuelResults, setShowDuelResults] = useState(false);
  const [selectedCompletedDuel, setSelectedCompletedDuel] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [achievementCard, setAchievementCard] = useState(null);
  const [showAchievementCard, setShowAchievementCard] = useState(false);
  const [newDuelInvite, setNewDuelInvite] = useState(null);

  useEffect(() => {
    if (duels?.length > 0) {
      const recentlyCompleted = duels?.find(
        d => d?.status === 'completed' &&
        new Date(d.updated_at) > new Date(Date.now() - 5000)
      );
      if (recentlyCompleted && !showDuelResults) {
        setSelectedCompletedDuel(recentlyCompleted);
        setShowDuelResults(true);
        checkDuelAchievement(recentlyCompleted);
      }
    }
  }, [duels]);

  const checkDuelAchievement = async (duel) => {
    if (!user?.id || !duel) return;
    try {
      const achievement = await achievementCardService?.checkDuelCompletion(user?.id, duel);
      if (achievement) {
        setAchievementCard(achievement);
        setTimeout(() => setShowAchievementCard(true), 2000);
      }
      const streakAchievement = await achievementCardService?.checkDuelStreak(user?.id);
      if (streakAchievement && !achievement) {
        setAchievementCard(streakAchievement);
        setTimeout(() => setShowAchievementCard(true), 2000);
      }
    } catch (error) {
      console.error('Error checking duel achievement:', error);
    }
  };

  const fetchSocialData = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const [challengesData, leaderboardData, milestonesData, badgesData, duelsData, squadsData, friendsData, friendActivityData] = await Promise?.all([
        challengeService?.getActiveChallenges(),
        friendshipService?.getFriendLeaderboard(user?.id, 'total_points'),
        milestoneService?.getFriendMilestones(user?.id, 20),
        achievementService?.getUserBadges(user?.id),
        duelService?.getUserDuels(user?.id),
        squadService?.getUserSquads(user?.id),
        friendshipService?.getFriends(user?.id),
        friendshipService?.getFriendWorkoutActivity(user?.id, 15)
      ]);
      setChallenges(challengesData);
      setLeaderboard(leaderboardData);
      setMilestones(milestonesData);
      setBadges(badgesData);
      setDuels(duelsData);
      setSquads(squadsData);
      setFriends(friendsData);
      setFriendActivity(friendActivityData || []);
    } catch (error) {
      console.error('Error fetching social data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchSocialData();
      checkPushNotificationPrompt();
    }
  }, [user?.id]);

  useUserDuelsRealtime(user?.id, {
    onDuelUpdate: async (payload) => {
      if (payload?.eventType === 'INSERT') {
        try {
          const duelsData = await duelService?.getUserDuels(user?.id);
          setDuels(duelsData);
        } catch (error) { console.error('Error refetching duels:', error); }
      } else if (payload?.eventType === 'UPDATE') {
        setDuels(prev => prev?.map(d => d?.id === payload?.new?.id ? { ...d, ...payload?.new } : d));
      } else if (payload?.eventType === 'DELETE') {
        setDuels(prev => prev?.filter(d => d?.id !== payload?.old?.id));
      }
    },
    onProofUpload: async () => {
      try {
        const duelsData = await duelService?.getUserDuels(user?.id);
        setDuels(duelsData);
      } catch (error) { console.error('Error refetching duels:', error); }
    }
  });

  useSocialRealtime(user?.id, {
    onDuelUpdate: async (payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
        try {
          const duelsData = await duelService?.getUserDuels(user?.id);
          setDuels(duelsData);
        } catch (error) { console.error('Error refetching duels:', error); }
      }
    },
    onFriendshipUpdate: async (payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
        try {
          const [friendsData, leaderboardData] = await Promise?.all([
            friendshipService?.getFriends(user?.id),
            friendshipService?.getFriendLeaderboard(user?.id, 'total_points')
          ]);
          setFriends(friendsData);
          setLeaderboard(leaderboardData);
        } catch (error) { console.error('Error refetching friends:', error); }
      }
    },
    onSquadUpdate: async (payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
        try {
          const squadsData = await squadService?.getUserSquads(user?.id);
          setSquads(squadsData);
        } catch (error) { console.error('Error refetching squads:', error); }
      }
    }
  });

  // ── Real-time: incoming duel invites ──
  useDuelInvitesRealtime(user?.id, async (payload) => {
    try {
      const duelsData = await duelService?.getUserDuels(user?.id);
      setDuels(duelsData);
      // Show invite banner for the new pending duel
      if (payload?.new) {
        setNewDuelInvite(payload?.new);
        setTimeout(() => setNewDuelInvite(null), 8000);
      }
    } catch (error) { console.error('Error refetching duels on invite:', error); }
  });

  // ── Real-time: leaderboard changes ──
  useLeaderboardRealtime(async () => {
    try {
      const leaderboardData = await friendshipService?.getFriendLeaderboard(user?.id, 'total_points');
      setLeaderboard(leaderboardData);
    } catch (error) { console.error('Error refetching leaderboard:', error); }
  });

  // ── Real-time: friend workout activity ──
  useFriendActivityRealtime(user?.id, async () => {
    try {
      const activityData = await friendshipService?.getFriendWorkoutActivity(user?.id, 15);
      setFriendActivity(activityData || []);
    } catch (error) { console.error('Error refetching friend activity:', error); }
  });

  useEffect(() => {
    if (!user?.id) return;
    const channel = socialRealtimeService?.subscribeToMilestones(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        setMilestones(prev => [payload?.new, ...prev]);
      }
    });
    return () => { channel?.unsubscribe(); };
  }, [user?.id]);

  const handleJoinChallenge = async (challengeId) => {
    try {
      await challengeService?.joinChallenge(user?.id, challengeId);
      fetchSocialData();
    } catch (error) { console.error('Error joining challenge:', error); }
  };

  const handleDuelCreated = () => { setShowCreateDuelModal(false); fetchSocialData(); };
  const handleSquadCreated = () => { setShowCreateSquadModal(false); fetchSocialData(); };
  const handleChallengeCreated = () => { setShowCreateChallengeModal(false); fetchSocialData(); };

  const handleDuelClick = (duel) => {
    if (duel?.status === 'completed') {
      setSelectedCompletedDuel(duel);
      setShowDuelResults(true);
    } else {
      setSelectedDuel(duel);
    }
  };

  const checkPushNotificationPrompt = async () => {
    if (!pushNotificationService?.isSupported()) return;
    const isSubscribed = await pushNotificationService?.isSubscribed();
    if (isSubscribed) return;
    const dismissedAt = localStorage.getItem('pushPromptDismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (new Date()?.getTime() - new Date(dismissedAt)?.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return;
    }
    setTimeout(() => setShowPushPrompt(true), 3000);
  };

  const handlePushPromptClose = (enabled) => {
    setShowPushPrompt(false);
    if (!enabled) {
      localStorage.setItem('pushPromptDismissed', new Date()?.toISOString());
    } else {
      localStorage.removeItem('pushPromptDismissed');
    }
  };

  const activeDuels = duels?.filter(d => d?.status === 'active') || [];
  const pendingDuels = duels?.filter(d => d?.status === 'pending') || [];
  const completedDuels = duels?.filter(d => d?.status === 'completed') || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="text-muted-foreground">Cargando Social+...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Social+ - Duelos, Amigos y Retos | VitalTrack</title>
        <meta name="description" content="Reta a tus amigos, compite en duelos, y gana stakes. Disciplina guiada con diversión." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* ── DUEL INVITE LIVE BANNER ── */}
          <AnimatePresence>
            {newDuelInvite && (
              <motion.div
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                className="mb-4 flex items-center gap-3 px-5 py-3 rounded-xl bg-warning/10 border border-warning/30 text-warning-foreground shadow-md"
              >
                <Icon name="Swords" size={18} className="text-warning shrink-0" />
                <p className="text-sm font-medium flex-1">
                  ¡Nueva invitación de duelo recibida!
                </p>
                <button
                  onClick={() => {
                    const invite = pendingDuels?.find(d => d?.id === newDuelInvite?.id) || pendingDuels?.[0];
                    if (invite) setSelectedDuel(invite);
                    setNewDuelInvite(null);
                  }}
                  className="text-xs font-semibold underline hover:no-underline shrink-0"
                >
                  Ver invitación
                </button>
                <button onClick={() => setNewDuelInvite(null)} className="ml-1 text-warning/60 hover:text-warning">
                  <Icon name="X" size={14} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── HERO BANNER ── */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-purple-600 p-6 mb-8 shadow-xl"
          >
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-extrabold text-white mb-1 flex items-center gap-3">
                  Social+
                  <span className="text-2xl">⚔️</span>
                </h1>
                <p className="text-primary-foreground/80 text-sm max-w-md">
                  Reta a tus socios · Compite en duelos · Gana o paga el stake
                </p>
              </div>
              <div className="flex items-center gap-5">
                <div className="text-center">
                  <p className="text-white/60 text-xs uppercase tracking-wide">Amigos</p>
                  <p className="text-2xl font-bold text-white">{friends?.length || 0}</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-white/60 text-xs uppercase tracking-wide">Puntos</p>
                  <p className="text-2xl font-bold text-white">{userProfile?.total_points || 0}</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-white/60 text-xs uppercase tracking-wide">Racha</p>
                  <p className="text-2xl font-bold text-yellow-300">{userProfile?.current_streak || 0} 🔥</p>
                </div>
              </div>
            </div>

            {/* Quick action pills */}
            <div className="relative mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => setShowCreateDuelModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-full transition-all backdrop-blur-sm border border-white/20"
              >
                <Icon name="Swords" size={15} />
                Nuevo Duelo
              </button>
              <button
                onClick={() => setShowFriendDiscovery(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-full transition-all backdrop-blur-sm border border-white/20"
              >
                <Icon name="UserPlus" size={15} />
                Agregar Amigo
              </button>
              <button
                onClick={() => { setActiveMainTab('squads'); setShowCreateSquadModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-full transition-all backdrop-blur-sm border border-white/20"
              >
                <Icon name="Users" size={15} />
                Crear Squad
              </button>
              <button
                onClick={() => navigate('/duel-history')}
                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-full transition-all backdrop-blur-sm border border-white/20"
              >
                <Icon name="History" size={15} />
                Historial
              </button>
            </div>
          </motion.div>

          {/* ── MAIN TAB NAVIGATION ── */}
          <div className="flex gap-1 p-1 bg-muted/30 rounded-xl border border-border mb-8 w-fit">
            <button
              onClick={() => setActiveMainTab('social')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeMainTab === 'social' ?'bg-card text-foreground shadow-sm border border-border' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Swords" size={16} />
              Social Feed
            </button>
            <button
              onClick={() => setActiveMainTab('squads')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeMainTab === 'squads' ?'bg-card text-foreground shadow-sm border border-border' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Users" size={16} />
              Squads
              {squads?.length > 0 && (
                <span className="ml-1 text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-medium">
                  {squads?.length}
                </span>
              )}
            </button>
          </div>

          {/* ── FRIEND REQUESTS (always visible) ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-8">
            <FriendRequestsPanel userId={user?.id} onRequestHandled={fetchSocialData} />
          </motion.div>

          {/* ── SQUADS TAB ── */}
          <AnimatePresence mode="wait">
            {activeMainTab === 'squads' && (
              <motion.div
                key="squads-tab"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon name="Users" size={18} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-foreground">My Squads</h2>
                      <p className="text-xs text-muted-foreground">{squads?.length || 0} active groups</p>
                    </div>
                  </div>
                  <Button onClick={() => setShowCreateSquadModal(true)} size="sm" iconName="Plus">
                    New Squad
                  </Button>
                </div>
                <SquadScreen
                  squads={squads}
                  userId={user?.id}
                  onSquadCreated={() => setShowCreateSquadModal(true)}
                  onUpdate={fetchSocialData}
                  challenges={challenges}
                  onJoinChallenge={handleJoinChallenge}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── SOCIAL FEED TAB ── */}
          <AnimatePresence mode="wait">
            {activeMainTab === 'social' && (
              <motion.div
                key="social-tab"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
              >
                {/* ── MAIN GRID: left column (2/3) + right column (1/3) ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                  {/* LEFT COLUMN */}
                  <div className="lg:col-span-2 space-y-10">

                    {/* ── DUELS SECTION ── */}
                    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                      <SectionHeader
                        icon="Swords"
                        title="Duelos"
                        subtitle={`${activeDuels?.length} activos · ${pendingDuels?.length} pendientes`}
                        action={
                          <Button onClick={() => setShowCreateDuelModal(true)} size="sm" iconName="Plus">
                            Crear Duelo
                          </Button>
                        }
                      />

                      {/* Duel Stats strip */}
                      <div className="mb-5">
                        <DuelStats duels={duels} userId={user?.id} badges={badges} compact />
                      </div>

                      {/* Active duels */}
                      {activeDuels?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          {activeDuels?.map((duel, i) => (
                            <DuelCard
                              key={duel?.id}
                              duel={duel}
                              userId={user?.id}
                              onClick={() => setSelectedDuel(duel)}
                              index={i}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 bg-muted/30 rounded-xl border border-dashed border-border mb-4">
                          <Icon name="Swords" size={40} className="text-muted-foreground mb-3" />
                          <p className="text-muted-foreground text-sm mb-3">No tienes duelos activos</p>
                          <Button onClick={() => setShowCreateDuelModal(true)} size="sm">Crear Primer Duelo</Button>
                        </div>
                      )}

                      {/* Pending invitations */}
                      {pendingDuels?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-warning mb-3 flex items-center gap-2">
                            <Icon name="Clock" size={14} />
                            Invitaciones Pendientes ({pendingDuels?.length})
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {pendingDuels?.map((duel, i) => (
                              <DuelCard
                                key={duel?.id}
                                duel={duel}
                                userId={user?.id}
                                onClick={() => setSelectedDuel(duel)}
                                index={i}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recently completed */}
                      {completedDuels?.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                              <Icon name="CheckCircle" size={14} />
                              Completados Recientemente
                            </p>
                            <button
                              onClick={() => navigate('/duel-history')}
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              Ver todo <Icon name="ArrowRight" size={12} />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {completedDuels?.slice(0, 4)?.map((duel, i) => (
                              <DuelCard
                                key={duel?.id}
                                duel={duel}
                                userId={user?.id}
                                onClick={() => {
                                  setSelectedCompletedDuel(duel);
                                  setShowDuelResults(true);
                                }}
                                index={i}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.section>

                    {/* ── FRIEND ACTIVITY FEED ── */}
                    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                      <SectionHeader
                        icon="Activity"
                        title="Actividad de Amigos"
                        subtitle="Duelos y logros recientes de tu red"
                      />
                      <FriendDuelActivityFeed
                        userId={user?.id}
                        onDuelClick={handleDuelClick}
                        onJoinDuel={() => fetchSocialData()}
                      />
                    </motion.section>

                    {/* ── CHALLENGES ── */}
                    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                      <SectionHeader
                        icon="Trophy"
                        title="Challenges Semanales"
                        subtitle="Únete y compite con amigos"
                        action={
                          <Button onClick={() => setShowCreateChallengeModal(true)} size="sm" variant="outline" iconName="Plus">
                            Crear
                          </Button>
                        }
                      />
                      {challenges?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {challenges?.map((challenge, i) => (
                            <ChallengeCard
                              key={challenge?.id}
                              challenge={challenge}
                              userId={user?.id}
                              onJoin={handleJoinChallenge}
                              index={i}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 bg-muted/30 rounded-xl border border-dashed border-border">
                          <Icon name="Trophy" size={40} className="text-muted-foreground mb-3" />
                          <p className="text-muted-foreground text-sm">No hay challenges activos en este momento</p>
                        </div>
                      )}
                    </motion.section>

                    {/* ── ACTIVITY FEED (milestones + workouts) ── */}
                    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                      <SectionHeader
                        icon="Zap"
                        title="Feed de Actividad"
                        subtitle="Hitos y entrenamientos de tu red social"
                      />
                      <ActivityFeed milestones={milestones} workoutActivity={friendActivity} userId={user?.id} />
                    </motion.section>
                  </div>

                  {/* RIGHT COLUMN */}
                  <div className="space-y-8">

                    {/* ── LEADERBOARD ── */}
                    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
                      <SectionHeader
                        icon="Award"
                        title="Ranking"
                        subtitle="Tu posición entre socios"
                      />
                      <LeaderboardCard leaderboard={leaderboard} currentUserId={user?.id} />
                    </motion.section>

                    {/* ── SQUADS PREVIEW ── */}
                    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
                      <SectionHeader
                        icon="Users"
                        title="Squads"
                        subtitle={`${squads?.length || 0} grupos activos`}
                        action={
                          <button
                            onClick={() => setActiveMainTab('squads')}
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            Ver todos <Icon name="ArrowRight" size={12} />
                          </button>
                        }
                      />
                      {squads?.length > 0 ? (
                        <div className="space-y-3">
                          {squads?.slice(0, 3)?.map((squad, i) => (
                            <button
                              key={squad?.id}
                              onClick={() => setActiveMainTab('squads')}
                              className="w-full flex items-center gap-3 p-3 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all text-left"
                            >
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                <Icon name={squad?.icon || 'Users'} size={20} className="text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-foreground text-sm truncate">{squad?.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{squad?.description || 'Squad group'}</p>
                              </div>
                              <Icon name="ChevronRight" size={16} className="text-muted-foreground shrink-0" />
                            </button>
                          ))}
                          {squads?.length > 3 && (
                            <button
                              onClick={() => setActiveMainTab('squads')}
                              className="w-full text-center text-xs text-primary hover:underline py-2"
                            >
                              +{squads?.length - 3} more squads
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 bg-muted/30 rounded-xl border border-dashed border-border">
                          <Icon name="Users" size={36} className="text-muted-foreground mb-3" />
                          <p className="text-muted-foreground text-sm mb-3 text-center">Crea un squad para competir en grupo</p>
                          <Button onClick={() => setShowCreateSquadModal(true)} size="sm" iconName="Plus">Crear Squad</Button>
                        </div>
                      )}
                    </motion.section>

                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Push Notification Prompt */}
          <AnimatePresence>
            {showPushPrompt && <PushNotificationPrompt onClose={handlePushPromptClose} />}
          </AnimatePresence>
        </main>

        {/* Modals */}
        <CreateDuelModal
          isOpen={showCreateDuelModal}
          onClose={() => setShowCreateDuelModal(false)}
          onDuelCreated={handleDuelCreated}
          userId={user?.id}
        />

        <CreateSquadModal
          isOpen={showCreateSquadModal}
          onClose={() => setShowCreateSquadModal(false)}
          onSquadCreated={handleSquadCreated}
          userId={user?.id}
        />

        <CreateChallengeModal
          isOpen={showCreateChallengeModal}
          onClose={() => setShowCreateChallengeModal(false)}
          userId={user?.id}
          onChallengeCreated={handleChallengeCreated}
          squads={squads}
          friends={friends}
        />

        <FriendDiscovery
          isOpen={showFriendDiscovery}
          onClose={() => setShowFriendDiscovery(false)}
          userId={user?.id}
          onFriendAdded={fetchSocialData}
        />

        {selectedDuel && (
          <DuelDetailModal
            isOpen={!!selectedDuel}
            onClose={() => setSelectedDuel(null)}
            duel={selectedDuel}
            userId={user?.id}
            onDuelUpdated={fetchSocialData}
          />
        )}

        {showDuelResults && selectedCompletedDuel && (
          <DuelResultsModal
            isOpen={showDuelResults}
            onClose={() => {
              setShowDuelResults(false);
              setSelectedCompletedDuel(null);
            }}
            duel={selectedCompletedDuel}
            userId={user?.id}
            onRematch={() => {
              setShowDuelResults(false);
              setShowCreateDuelModal(true);
            }}
            onChallengeAgain={() => {
              setShowDuelResults(false);
              setShowCreateDuelModal(true);
            }}
          />
        )}
      </div>
    </>
  );
};

export default SocialHub;